import { Location } from '@angular/common';
import { ChangeDetectorRef, Component, Input } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { DialogService } from '@progress/kendo-angular-dialog';
import { BadgeAlign } from '@progress/kendo-angular-indicators';
import { NotificationService } from '@progress/kendo-angular-notification';
import { Observable, combineLatest, filter, finalize, map, of, takeUntil, tap } from 'rxjs';
import { AgencyList } from 'src/app/modules/dashboard/interface/agency-list';
import { LoadingService } from 'src/app/services/loading.service';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { AbstractForm } from 'src/app/shared/components/abstract-form';
import { timeRangeValidator } from 'src/app/shared/functions/timeRangeValidator';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { ISedmsFormModel } from 'src/app/shared/interfaces/form/sedms-form-model';
import { User } from 'src/app/shared/models/user';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { AgencyLocationInfo } from '../../../../modules/administration/constants/agency-location';
import { AdminService } from '../../../../modules/administration/services/admin.service';
import { AgencyAdminFormModel } from '../../../../modules/dashboard/models/agency-admin/agency-admin-formmodel';
import { AgencyAdminRefData } from '../../../../modules/dashboard/models/agency-admin/agency-admin-reference.model';
import { AgencyAdminRequestModel } from '../../../../modules/dashboard/models/agency-admin/agency-admin-request-model';
import { AgencyAdminSubmitModel } from '../../../../modules/dashboard/models/agency-admin/agency-admin-submit-model';

@Component({
  selector: 'app-capacity-info',
  templateUrl: './capacity-info.component.html',
  styleUrls: ['./capacity-info.component.scss'],
  standalone: false,
})

export class CapacityInfoComponent extends AbstractForm<
  AgencyAdminRequestModel,
  AgencyAdminRequestModel,
  AgencyAdminRefData
> {
  form: FormGroup;
  getAgencyDetails: any;
  startTimePickerMin: Date = new Date(2000, 1, 1, 8, 0);
  startTimePickerMax: Date = new Date(2000, 1, 1, 16, 30);
  endTimePickerMin: Date = new Date(2000, 1, 1, 8, 30);
  endTimePickerMax: Date = new Date(2000, 1, 1, 17, 0);
  @Input() locationDetails: AgencyList;
  public gridData: any[] = [
    { id: 1, name: 'John Doe', age: 30 },
    { id: 2, name: 'Jane Smith', age: 25 },
    { id: 3, name: 'David Johnson', age: 35 },
  ];

  loadSpinner: boolean;
  public show: boolean = false;
  formModel: ISedmsFormModel = AgencyAdminFormModel;
  public badgeAlign: BadgeAlign = { vertical: 'bottom', horizontal: 'end' };
  agencyId: string;
  user: User;
  userDetails: User;
  action: string;
  public value: Date;
  isEdit: boolean;
  agencyLocation: any;
  viewAction: string = 'view';
  private initialized = false;
  frequencyInterval: IReferenceData[] = [];
  waitTimeMap = new Map<number, string>();
  operatingMDEProgram: any[] = [{ id: 'true', description: 'Yes' }, { id: 'false', description: 'No' }];
  constructor(
    loadingService: LoadingService,
    library: FaIconLibrary,
    cdRef: ChangeDetectorRef,
    formBuilder: FormBuilder,
    dialogService: DialogService,
    notificationService: NotificationService,
    private authStore: AuthStore,
    private adminService: AdminService,
    private referenceStore: ReferenceStore,
    route: ActivatedRoute,
    private routeConstantService: RoutesConstantService,
    public router: Router,
    private location: Location
  ) {
    super(
      loadingService,
      cdRef,
      route,
      formBuilder,
      dialogService,
      notificationService
    );
    library.addIcons(
      faCalendar
      // faTreeCity
    );
    this.user = this.authStore.UserInfo;
    this.authStore.UserInfo$.pipe(
      filter((user: User) => !!user && !!user?.selectedRole),
      takeUntil(this.componentDestroyed)
    ).subscribe((user: User) => {
      this.userDetails = user;
    });
    this.route.params
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((params: Params) => {
        this.agencyId = params.id;
        this.action = params.action;

        if (!this.initialized) {
          this.isEdit = false;
          this.initialized = true;
        }
      });
  }

  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.language$,
      this.referenceStore.state$,
      this.referenceStore.frequencyInterval$,
      this.adminService.getAllAgencyLocations(this.agencyId),
    ]) as any;
  }

  initEntity(
    entityData: AgencyList,
    referenceData: AgencyAdminRefData
  ): AgencyAdminRequestModel {
    return new AgencyAdminRequestModel(
      entityData,
      referenceData.agencyLocation
    );
  }

  get entity$(): Observable<AgencyAdminRequestModel> {
    const sk = this.agencyId;

    if (!sk) {
      return of({} as AgencyAdminRequestModel);
    }

    return (this.adminService.getMdeAgencyByParentAgencySk(sk) as any).pipe(
      tap((data: any) => {
        this.entityData = data as any;

        if (!this.agencyLocation || !this.form) return;

        this.form.patchValue(
          new AgencyAdminRequestModel(data, this.agencyLocation),
          { emitEvent: false }
        );
      }),
      map((data: any) => data ?? ({} as AgencyAdminRequestModel))
    );
  }

  initReferenceData([language, state, frequencyInterval, agencyLocation]: [
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    AgencyLocationInfo[]
  ]): AgencyAdminRefData {
    this.frequencyInterval = frequencyInterval
      .filter(item => [5, 6, 7].includes(Number(item.id)))
      .map(item => ({
        ...item,
        description: `${item.description}(s)`
      }));

    this.frequencyInterval.forEach(x =>
      this.waitTimeMap.set(Number(x.id), x.description.replace('(s)', ''))
    );
    return { language, state, frequencyInterval, agencyLocation };
  }

  initWatches(): void {
    if (this.locationDetails?.agencySk) {
      this.adminService
        .getAllAgencyLocations(this.locationDetails?.agencySk)
        .subscribe((data) => {
          this.agencyLocation = data;
          const agencyLocation = this.agencyLocation.find(
            (d) => d.agencyLocationSK == this.locationDetails.agencyLocationSk
          );
          if (agencyLocation) {
            this.form.get('location')?.setValue(agencyLocation);
          }
        });
    }
  }

  public selectionChange(value: any): void {
    this.adminService
      .getMDEAgencyPerLocation(value?.agencyLocationSK)
      .subscribe((details) => {
        const agencyLocation = this.agencyLocation.find(
          (d) => d.agencyLocationSK == value?.agencyLocationSK
        );
        const data: AgencyAdminRequestModel = new AgencyAdminRequestModel(details, this.agencyLocation);
        this.form.patchValue(data, { emitEvent: false });
        this.loadSpinner = false;
      });
  }

  public override otherInitalizations(): void {
    this.form.setValidators([
      timeRangeValidator('serviceStartTime', 'serviceEndTime'),
      timeRangeValidator('lunchStartTime', 'lunchEndTime'),
    ]);

    // Service time 
    this.form.get('serviceStartTime')?.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(start => {
        const end = this.form.get('serviceEndTime')?.value;
        if (start && end && start > end) {
          this.form.get('serviceEndTime')?.setValue(null);
        }
      });

    // Lunch time 
    this.form.get('lunchStartTime')?.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(start => {
        const end = this.form.get('lunchEndTime')?.value;
        if (start && end && start > end) {
          this.form.get('lunchEndTime')?.setValue(null);
        }
      });

    this.form.get('dateFrequency')?.valueChanges
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe(() => {

        const numberControl = this.form.get('averageWaitTime');
        const max = this.getMaxWaitTime();

        numberControl?.setValidators([
          Validators.required,
          Validators.min(1),
          Validators.max(max!)
        ]);

        numberControl?.updateValueAndValidity();
      });
    this.form.get('location')?.enable({ emitEvent: false });
    this.form.updateValueAndValidity();
  }


  // getMdeAgencyByAgencysk() {
  //   this.dashboardService.getMdeAgencyByAgencySk(this.agencyId).subscribe(data => {
  //     this.getAgencyDetails = data;
  //   })
  // }

  postCancelConfirmation(): void {
    this.isEdit = false;
    this.loadSpinner = false;
  }

  cancelEdit(): void {
    this.isEdit = false;
    this.form.get('location')?.enable({ emitEvent: false });
  }

  postSubmitConfirmation(): void {
    this.isEdit = false;

    // changing route url to view without reloading the component
    if (this.router.url.includes('agency-mde')) {
      this.location.replaceState(
        `/admin/agency-mde/${this.agencyId}/view`
      );
    }
    else {
      this.location.replaceState(
        `/location/${this.agencyId}/view`
      );
    }

  }


  saveDetails(): Observable<unknown> {

    this.loadSpinner = true;

    const req: AgencyAdminSubmitModel = new AgencyAdminSubmitModel({
      ...this.form.value,
    });

    req.agencySk = this.agencyId;
    req.agencyLocationSk = this.entityData.agencyLocationSk;
    req.agencyTypeId = this.entityData.agencyTypeId;

    this.adminService.saveMdeAgency(req)
      .pipe(finalize(() => this.loadSpinner = false))
      .subscribe((res) => {

        const data = Array.isArray(res)
          ? res?.find(d => d.agencyLocationSk === this.entityData.agencyLocationSk)
          : res;

        this.entityData = data;

        const model = new AgencyAdminRequestModel(data, this.agencyLocation);
        this.form.patchValue(model, { emitEvent: false });

        setTimeout(() => {
          this.postSubmitConfirmation();
        }, 0);

      });

    return of(null);
  }

  getTime(controlName: string): Date | null {
    return this.form.get(controlName)?.value || null;
  }

  get canSaveDraft(): boolean {
    return this.form?.valid;
  }

  get canSubmit(): boolean {
    return this.form?.valid || this.action === this.viewAction;
  }

  public togglePopup(): void {
    this.show = !this.show;
  }

  onEditCapacity(): void {
    if (this.isEdit) return;
    this.isEdit = true;
    this.form.enable({ emitEvent: false });
    if (this.router.url.includes('agency-mde')) {
      this.router.navigate(
        ['/admin/agency-mde', this.agencyId, 'edit'],
        { replaceUrl: true }
      );
    }
    else {
      this.router.navigate(
        ['/location', this.agencyId, 'edit'],
        { replaceUrl: true }
      );
    }
  }

  commaSeparateValues(fields: { key: string; label: string }[]): string {
    return fields
      .filter(
        (field: { key: string; label: string }) => this.entityData?.[field.key]
      )
      .map((field: { key: string; label: string }) => field.label)
      .join(', ');
  }
  getMaxWaitTime(): number | null {
    const unit = this.form?.get('dateFrequency')?.value;

    if (unit == 5) return 365;   // Days
    if (unit == 6) return 52;    // Weeks
    if (unit == 7) return 12;    // Months

    return null;
  }
}
